import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, FileText, CheckCircle2, AlertCircle, Lock, Sparkles, ShieldCheck, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { markFirstValueTracked } from '@/utils/attribution';

const ScoreRing = ({ score }) => {
  const color = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';
  const label = score >= 75 ? 'Strong' : score >= 50 ? 'Average' : 'Needs Work';
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-28 h-28">
        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-muted/20" />
          <circle
            cx="18" cy="18" r="15.9" fill="none"
            stroke={color} strokeWidth="2.5"
            strokeDasharray={`${score} ${100 - score}`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color }} data-testid="scanner-score">{score}</span>
          <span className="text-[10px] text-muted-foreground">/ 100</span>
        </div>
      </div>
      <span className="text-sm font-semibold" style={{ color }}>{label}</span>
    </div>
  );
};

const CategoryBar = ({ label, score, max }) => {
  const pct = Math.round((score / max) * 100);
  const color = pct >= 75 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-400';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{score}/{max}</span>
      </div>
      <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const ResumeScanner = () => {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [scanCount, setScanCount] = useState(null); // null = loading; 0 = never scanned
  const { user, token, api } = useAuth();
  const navigate = useNavigate();

  // Fetch existing scan count for logged-in users (to show nudge)
  useEffect(() => {
    if (!user || !token) return;
    api.get('/ats-history')
      .then(res => setScanCount((res.data?.history || []).length))
      .catch(() => setScanCount(0));
  }, [user, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileUpload = async (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.match(/\.(pdf|docx)$/i)) {
      toast.error('Please upload PDF or DOCX only');
      return;
    }
    if (uploadedFile.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB.');
      return;
    }

    const scanStartedAt = performance.now();
    const fileType = uploadedFile.name.toLowerCase().endsWith('.pdf') ? 'pdf' : 'docx';
    const sizeBucket = uploadedFile.size < 500000 ? 'under_500kb' : uploadedFile.size < 2000000 ? '500kb_to_2mb' : '2mb_to_5mb';
    if (window.trackConversion) {
      window.trackConversion('scan_started', { source: 'public_scanner', file_type: fileType, size_bucket: sizeBucket });
    }

    setFile(uploadedFile);
    setScanning(true);
    setError(null);

    try {
      const fd = new FormData();
      fd.append('file', uploadedFile);
      // Pass auth token so logged-in users get scan history saved automatically
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      // Railway serves this through the frontend's same-origin /api proxy. Other hosts can still set an explicit backend URL.
      const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '');
      const apiBase = configuredBackendUrl ? `${configuredBackendUrl}/api` : '/api';
      const res = await fetch(`${apiBase}/scan-resume`, {
        method: 'POST',
        body: fd,
        headers,
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || 'Scan failed. Try a different file.';
        throw new Error(detail);
      }
      const data = await res.json();
      setResults(data);
      if (window.trackConversion) {
        const scoreBand = data.score >= 75 ? 'strong' : data.score >= 50 ? 'average' : 'needs_work';
        window.trackConversion('resume_uploaded', { file_type: fileType, size_bucket: sizeBucket });
        window.trackConversion('scan_completed', {
          score_band: scoreBand,
          duration_ms: Math.round(performance.now() - scanStartedAt),
        });
        if (markFirstValueTracked()) {
          window.trackConversion('first_value', { value_type: 'ats_score', score_band: scoreBand });
        }
      }
    } catch (err) {
      setError(err.message || 'Scan failed. Try a different file.');
      toast.error(err.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const withAttribution = (path, initialParams = {}) => {
    const destination = new URLSearchParams(initialParams);
    const incoming = new URLSearchParams(window.location.search);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'].forEach((key) => {
      const value = incoming.get(key);
      if (value) destination.set(key, value);
    });
    return `${path}?${destination.toString()}`;
  };

  const handleSignupToSeeImprovements = () => {
    if (window.trackConversion) {
      window.trackConversion('signup_from_scanner');
    }
    navigate(withAttribution('/auth', { source: 'scanner', mode: 'signup', next: '/scanner' }));
  };

  const handleUpgradeToPremium = () => {
    if (window.trackConversion) {
      window.trackConversion('upgrade_clicked', { source: 'scanner' });
    }
    navigate(withAttribution('/pricing', { source: 'scanner' }));
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl sm:text-3xl flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-primary" />
          Free CV check
        </CardTitle>
        <p className="text-sm text-muted-foreground">Check formatting and keywords.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {!results ? (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer">
              <input
                type="file"
                id="resume-upload"
                accept=".pdf,.docx"
                onChange={handleFileUpload}
                className="hidden"
                disabled={scanning}
              />
              <label htmlFor="resume-upload" className="cursor-pointer">
                {scanning ? (
                  <div className="space-y-3">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-sm font-medium">Scanning your resume...</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-12 h-12 text-muted-foreground mx-auto" />
                    <div>
                      <p className="text-sm font-medium">Click to upload your resume</p>
                      <p className="text-xs text-muted-foreground mt-1">PDF or DOCX • Max 5MB</p>
                    </div>
                  </div>
                )}
              </label>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Lock className="w-3 h-3" />
              <span>For anonymous scans, your uploaded file is analysed in memory and is not saved. <a href="/privacy" className="underline hover:text-foreground">Privacy</a></span>
            </div>
            {/* Nudge: logged-in user has never scanned — starts the ATS history chart */}
            {user && scanCount === 0 && !scanning && (
              <div
                className="flex items-start gap-3 rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-xs"
                data-testid="scanner-first-scan-nudge"
              >
                <TrendingUp className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-300">Your ATS score trend starts here</p>
                  <p className="text-muted-foreground mt-0.5">Scan now and every score you get is tracked on your dashboard — so you can see yourself improving over time.</p>
                </div>
              </div>
            )}
            {error && (
              <div className="text-xs text-red-500 bg-red-500/10 border border-red-500/30 rounded p-2" data-testid="scanner-error">
                {error}
              </div>
            )}
          </div>
        ) : results.rate_limited ? (
          /* Dedicated upsell card when free quota is used up */
          <div className="space-y-6" data-testid="scanner-rate-limited">
            <div className="text-center p-6 bg-gradient-to-br from-orange-500/10 to-primary/5 rounded-lg border border-orange-500/30">
              <div className="w-14 h-14 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-3">
                <Lock className="w-7 h-7 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">{results.message || "You've hit your free scan limit"}</h3>
              <p className="text-sm text-muted-foreground mb-4">{results.upsell}</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button onClick={handleSignupToSeeImprovements} className="bg-primary hover:bg-primary/90" data-testid="rate-limit-signup-btn">
                  Sign Up Free — See Full Diagnosis
                </Button>
                <Button variant="outline" onClick={() => { setFile(null); setResults(null); }}>
                  Try again in 1 hour
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Everyone gets the score; detailed category scoring belongs to the signed-in report. */}
            <div className="flex flex-col sm:flex-row items-center gap-6 p-5 bg-muted/10 rounded-xl border border-border">
              <ScoreRing score={results.score} />
              {user ? (
                <div className="flex-1 space-y-3 w-full">
                  <p className="text-xs text-muted-foreground mb-1 font-medium uppercase tracking-wide">Full score breakdown</p>
                  {results.breakdown && Object.values(results.breakdown).map((cat) => (
                    <CategoryBar key={cat.label} label={cat.label} score={cat.score} max={cat.max} />
                  ))}
                </div>
              ) : (
                <div className="flex-1 w-full text-center sm:text-left">
                  <p className="font-semibold">Your first ATS signal is ready.</p>
                  <p className="text-sm text-muted-foreground mt-1">Create a free account to reveal the category breakdown and every issue we found.</p>
                </div>
              )}
            </div>

            {/* What ATS Found */}
            {results.parsed?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  {user ? 'What ATS Systems Found' : 'One thing ATS detected'}
                </h3>
                <ul className="space-y-1.5">
                  {(user ? results.parsed : results.parsed.slice(0, 1)).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* What's Missing */}
            {results.missing?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  {user ? 'Critical Gaps' : 'One important gap to fix'}
                </h3>
                <ul className="space-y-1.5">
                  {(user ? results.missing : results.missing.slice(0, 1)).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Quick Wins Preview */}
            {user && results.improvements_preview?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Quick Wins (preview)
                </h3>
                <ul className="space-y-1.5">
                  {results.improvements_preview.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Gated full report */}
            {!user ? (
              <div className="bg-muted/30 rounded-lg p-6 text-center space-y-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Lock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">
                    We found {Math.max(1, (results.improvements_count || 0) + Math.max(0, (results.parsed?.length || 0) - 1) + Math.max(0, (results.missing?.length || 0) - 1))} more improvements
                  </h3>
                  <p className="text-sm text-muted-foreground">Create a free account to see your full diagnosis. No payment is required to view the report.</p>
                </div>
                <Button onClick={handleSignupToSeeImprovements} className="w-full" data-testid="scanner-signup-btn">
                  Create Free Account to See Full Diagnosis
                </Button>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg p-6 text-center space-y-4 border border-primary/20">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Ready for AI to fix these?</h3>
                  <p className="text-sm text-muted-foreground">AI rewrites and fixes are available on a paid plan.</p>
                </div>
                <Button onClick={handleUpgradeToPremium} className="w-full bg-primary hover:bg-primary/90" data-testid="scanner-tailor-btn">
                  View plans for AI fixes
                </Button>
              </div>
            )}

            <Button variant="outline" onClick={() => { setFile(null); setResults(null); setError(null); }} className="w-full">
              Scan Another Resume
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ResumeScanner;
